// ─────────────────────────────────────────────
//  GoHomeAlert — "Leaving HH:MM" badge for a CRNA near go-home (D-04)
//  Threshold + label logic live in src/domain/schedule.js (single source
//  of truth) — this component renders, it does not compute. The primary
//  signal is badge TEXT + color; the atcPulse ring is a secondary signal
//  applied only when prefers-reduced-motion is no-preference (WCAG 2.3.3).
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import { isNearGoHome, goHomeAlertLabel } from "../domain/schedule";

const { font, radius } = tokens;

// Guarded matchMedia read (same pattern as BreakStatusRow).
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function GoHomeAlert({ goHomeHour, shiftCode, now }) {
  // Prefer shiftCode when provided; fall back to legacy goHomeHour.
  const value = shiftCode ?? goHomeHour;
  if (!isNearGoHome(value, now)) return null;

  const label = goHomeAlertLabel(value);
  // Non-time codes (ASAP, range-with-no-end-hour) return ""; render nothing.
  if (!label) return null;

  const reduceMotion = prefersReducedMotion();

  return (
    <span
      aria-label={`Leaving at ${label.replace("Leaving ", "")}`}
      style={{
        ...styles.badge,
        // Smooth pulse, never a blink; suppressed entirely under reduce.
        ...(reduceMotion ? null : { animation: "atcPulse 1.4s ease-in-out infinite" }),
      }}
    >
      {label}
    </span>
  );
}

const styles = {
  badge: {
    display: "inline-block",
    background: "#fef8ec",
    // Amber-on-white must be #d97706 (6.4:1), NOT #f59e0b (fails 4.5:1).
    border: "2px solid #d97706",
    color: "#7a4f00",
    borderRadius: radius.full,
    padding: "2px 10px",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    fontFamily: font.family,
    whiteSpace: "nowrap",
  },
};
